import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  AlertTriangle,
  Search,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Info,
  Download,
  Filter
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const Diagnostics = ({ connectionStatus }) => {
  const [dtcs, setDtcs] = useState([]);
  const [selectedDTCs, setSelectedDTCs] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearConfirmation, setClearConfirmation] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterModule, setFilterModule] = useState("all");
  const [dtcDatabase, setDtcDatabase] = useState({});
  const [selectedDTC, setSelectedDTC] = useState(null);

  useEffect(() => {
    fetchDTCDatabase();
  }, []);

  const fetchDTCDatabase = async () => {
    try {
      const response = await axios.get(`${API}/dtc/database`);
      setDtcDatabase(response.data);
    } catch (error) {
      console.error("Failed to fetch DTC database:", error);
    }
  };

  const scanDTCs = async () => {
    if (!connectionStatus.connected) {
      toast.error("No vehicle connected");
      return;
    }

    setIsScanning(true);
    try {
      const response = await axios.get(`${API}/dtc/scan`);
      setDtcs(response.data);
      toast.success(`Scan complete: ${response.data.length} codes found`);
    } catch (error) {
      toast.error("DTC scan failed", {
        description: error.response?.data?.detail || "Unable to scan vehicle"
      });
    } finally {
      setIsScanning(false);
    }
  };

  const clearSelectedDTCs = async () => {
    if (clearConfirmation !== "CLEAR_DTC_CONFIRM") {
      toast.error("Invalid confirmation code");
      return;
    }

    try {
      await axios.post(`${API}/dtc/clear`, {
        code_ids: selectedDTCs,
        confirmation_code: clearConfirmation
      });
      
      setDtcs(dtcs.filter(dtc => !selectedDTCs.includes(dtc.id)));
      setSelectedDTCs([]);
      setShowClearDialog(false);
      setClearConfirmation("");
      toast.success("DTCs cleared successfully");
    } catch (error) {
      toast.error("Failed to clear DTCs", {
        description: error.response?.data?.detail
      });
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "critical":
        return <span className="badge-error">Critical</span>;
      case "warning":
        return <span className="badge-warning">Warning</span>;
      default:
        return <span className="badge-info">Info</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <span className="badge-error">Active</span>;
      case "pending":
        return <span className="badge-warning">Pending</span>;
      default:
        return <span className="bg-white/10 text-white/60 px-2 py-1 rounded text-xs uppercase">History</span>;
    }
  };

  const filteredDTCs = dtcs.filter(dtc => {
    if (filterSeverity !== "all" && dtc.severity !== filterSeverity) return false;
    if (filterModule !== "all" && dtc.module !== filterModule) return false;
    return true;
  });

  const modules = [...new Set(dtcs.map(dtc => dtc.module))];

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDTCs(filteredDTCs.map(dtc => dtc.id));
    } else {
      setSelectedDTCs([]);
    }
  };

  const handleSelectDTC = (dtcId, checked) => {
    if (checked) {
      setSelectedDTCs([...selectedDTCs, dtcId]);
    } else {
      setSelectedDTCs(selectedDTCs.filter(id => id !== dtcId));
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Diagnostic Trouble Codes</h1>
          <p className="header-subtitle">Scan, analyze, and clear vehicle fault codes</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Button
            onClick={scanDTCs}
            disabled={!connectionStatus.connected || isScanning}
            className="btn-primary"
            data-testid="scan-dtc-btn"
          >
            {isScanning ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Scan Vehicle
              </>
            )}
          </Button>
          
          {selectedDTCs.length > 0 && (
            <Button
              onClick={() => setShowClearDialog(true)}
              className="btn-destructive"
              data-testid="clear-dtc-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Clear Selected ({selectedDTCs.length})
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-white/50" />
          <span className="text-sm text-white/50">Filters:</span>
        </div>
        
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-40 bg-black/50 border-white/10 text-white" data-testid="filter-severity">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E1E1E] border-white/10">
            <SelectItem value="all" className="text-white">All Severities</SelectItem>
            <SelectItem value="critical" className="text-white">Critical</SelectItem>
            <SelectItem value="warning" className="text-white">Warning</SelectItem>
            <SelectItem value="info" className="text-white">Info</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterModule} onValueChange={setFilterModule}>
          <SelectTrigger className="w-48 bg-black/50 border-white/10 text-white" data-testid="filter-module">
            <SelectValue placeholder="Module" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E1E1E] border-white/10">
            <SelectItem value="all" className="text-white">All Modules</SelectItem>
            {modules.map(module => (
              <SelectItem key={module} value={module} className="text-white">{module}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="ml-auto text-sm text-white/50 font-mono">
          {filteredDTCs.length} codes displayed
        </span>
      </div>

      {/* DTC Table */}
      <div className="bento-card">
        {!connectionStatus.connected ? (
          <div className="text-center py-16">
            <AlertTriangle className="h-16 w-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/50 text-lg">No vehicle connected</p>
            <p className="text-white/30 text-sm mt-2">Connect to your Tesla to scan for diagnostic codes</p>
          </div>
        ) : dtcs.length === 0 ? (
          <div className="text-center py-16">
            <Search className="h-16 w-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/50 text-lg">No codes found</p>
            <p className="text-white/30 text-sm mt-2">Click "Scan Vehicle" to check for diagnostic trouble codes</p>
          </div>
        ) : (
          <table className="dtc-table" data-testid="dtc-table">
            <thead>
              <tr>
                <th className="w-12">
                  <Checkbox
                    checked={selectedDTCs.length === filteredDTCs.length && filteredDTCs.length > 0}
                    onCheckedChange={handleSelectAll}
                    className="border-white/30"
                    data-testid="select-all-dtc"
                  />
                </th>
                <th>Code</th>
                <th>Description</th>
                <th>Module</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filteredDTCs.map((dtc) => (
                <tr 
                  key={dtc.id} 
                  className="cursor-pointer hover:bg-white/5"
                  onClick={() => setSelectedDTC(dtc)}
                  data-testid={`dtc-row-${dtc.code}`}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedDTCs.includes(dtc.id)}
                      onCheckedChange={(checked) => handleSelectDTC(dtc.id, checked)}
                      className="border-white/30"
                    />
                  </td>
                  <td className="dtc-code">{dtc.code}</td>
                  <td className="max-w-xs truncate">{dtc.description}</td>
                  <td>{dtc.module}</td>
                  <td>{getSeverityBadge(dtc.severity)}</td>
                  <td>{getStatusBadge(dtc.status)}</td>
                  <td className="text-white/50 text-xs">
                    {new Date(dtc.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* DTC Detail Dialog */}
      {selectedDTC && (
        <Dialog open={!!selectedDTC} onOpenChange={() => setSelectedDTC(null)}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-3">
                <span className="text-[#007AFF]">{selectedDTC.code}</span>
                {getSeverityBadge(selectedDTC.severity)}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs text-white/50 uppercase tracking-wider">Description</label>
                <p className="text-white mt-1">{selectedDTC.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/50 uppercase tracking-wider">Module</label>
                  <p className="font-mono text-white mt-1">{selectedDTC.module}</p>
                </div>
                <div>
                  <label className="text-xs text-white/50 uppercase tracking-wider">Status</label>
                  <p className="mt-1">{getStatusBadge(selectedDTC.status)}</p>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-white/50 uppercase tracking-wider">First Detected</label>
                <p className="font-mono text-white/80 mt-1">
                  {new Date(selectedDTC.timestamp).toLocaleString()}
                </p>
              </div>
              
              {selectedDTC.severity === "critical" && (
                <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-[#FF3B30] mt-0.5" />
                    <div>
                      <p className="text-[#FF3B30] font-medium">Critical Fault</p>
                      <p className="text-white/60 text-sm mt-1">
                        This code indicates a critical system fault. Professional diagnosis recommended before driving.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSelectedDTC(null)}
                className="bg-transparent border-white/20 text-white hover:bg-white/5"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Clear DTCs Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF3B30]">
              Clear Diagnostic Codes
            </DialogTitle>
            <DialogDescription className="text-white/60">
              You are about to clear {selectedDTCs.length} diagnostic trouble code(s). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded warning-flash">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#FF3B30] mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#FF3B30] font-medium">Warning</p>
                  <p className="text-white/60 mt-1">
                    Clearing codes without fixing the underlying issue may result in the codes returning. Ensure all repairs are complete before clearing.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-white/60 mb-2 block">
                Type <span className="font-mono text-[#007AFF]">CLEAR_DTC_CONFIRM</span> to confirm:
              </label>
              <input
                type="text"
                value={clearConfirmation}
                onChange={(e) => setClearConfirmation(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-mono focus:border-[#FF3B30] focus:outline-none"
                placeholder="Enter confirmation code"
                data-testid="clear-confirmation-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowClearDialog(false);
                setClearConfirmation("");
              }}
              className="bg-transparent border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={clearSelectedDTCs}
              disabled={clearConfirmation !== "CLEAR_DTC_CONFIRM"}
              className="btn-destructive"
              data-testid="confirm-clear-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Clear Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Diagnostics;
